import { Injectable } from '@angular/core';
import { ApiHelperService } from '../../core/api-helper.service';
import * as _ from 'lodash';
import { ComparisonFeeGroupings } from '../comparison-fee-groupings/comparison-fee-groupings.model';
import { CostComparison } from './cost-comparison.model';
import { FeesSummaryService } from '../../core/fees-summary-service/fees-summary.service';

@Injectable({
    providedIn: 'root'
})
export class CostComparisonService {

    options = { authenticate: true, setRoot: true };

    constructor( private api: ApiHelperService, private feesSummaryService: FeesSummaryService) { }

    getCostComparisionById(id: string): Promise<CostComparison>{
        return this.api.apiCallSane(
            '/cost-comparisons',
            'GET',
            this.api.getDefaultHeaders(),
            { id },
            null,
            this.options)
            .then((c: Partial<CostComparison>)  => new CostComparison(c));
    }

    getProposalCostComparisons(proposalId: string): Promise<CostComparison[]> {
        return this.api.apiCallSane(
            '/cost-comparisons',
            'GET',
            this.api.getDefaultHeaders(),
            { proposalId },
            null,
            this.options)
            .then((ccs: Partial<CostComparison>[]) => ccs.map(c => new CostComparison(c)));
    }

    addCostComparisonToProposal(
        proposalId: string,
        costComparisonObjects,
        costComparisonEditForms): Promise<CostComparison[]> 
    {
        return Promise.all(costComparisonObjects.map((costComparisonObj, index) => {
            const formValues = costComparisonEditForms[index].value;

            return this.api.apiCallSane(
                '/cost-comparison',
                'PUT',
                this.api.getDefaultHeaders(),
                null,
                {
                    proposalId,
                    statementId: costComparisonObj.statementData.statementId,
                    friendlyName: formValues.friendlyName,
                    notes: formValues.notes ? formValues.notes : "",
                    createdAt: new Date(),
                    statementData: costComparisonObj.statementData,
                    originalStructure: costComparisonObj.originalStructure,
                    proposedStructure: costComparisonObj.proposedStructure,
                    originalFeeGroupings: costComparisonObj.originalFeeGroupings,
                    comparisonFeeGroupings: costComparisonObj.comparisonFeeGroupings
                } as Partial<CostComparison>,
                this.options)
                .then((c: Partial<CostComparison>) => new CostComparison(c));
        }));
    }

    update(id: string, updateData: Partial<CostComparison>): Promise<CostComparison> {
        return this.api.apiCallSane(
            '/cost-comparison',
            'PATCH',
            this.api.getDefaultHeaders(),
            { id },
            updateData,
            this.options)
            .then((cc: Partial<CostComparison>) => new CostComparison(cc));
    }

    deleteCostComparisonFromProposal(id: string): Promise<CostComparison> {
        return this.api.apiCallSane(
            '/cost-comparison',
            'DELETE',
            this.api.getDefaultHeaders(),
            { id },
            null,
            this.options);
    }

    /**
     * Extracts fees from the cost comparison, creating a csv-formatted string
     * with double-quoted fields.
     */
    exportCostComparisonAsCsv(comp: CostComparison): string {
        const fees: ComparisonFeeGroupings = comp.comparisonFeeGroupings;

        let csvAsString = '"label","type","amount","proposedAmount","volume","transactionCount","transactionRate","transactionFee","proposedRate","proposedFee"\n';

        // The fields for ProcessorComparisonFee don't match the rest
        fees.processor.fees?.forEach(fee => {
            if (fee.feeDetails) {
                fee.feeDetails.forEach(fd =>
                    csvAsString += `"${fd.description.trim()}",`
                    + `"processor",`
                    + `"${fd.amount}",`
                    + `"${fd.proposedAmount}",`
                    + `"${fd.volume ?? ''}",`
                    + `"${fd.monthlyTransactions ?? ''}",`
                    + `"${fd.transactionRate ?? ''}",`
                    + `"${fd.transactionFee ?? ''}",`
                    + `"${fd.proposedRate ?? ''}",`
                    + `"${fd.proposedFee ?? ''}"\n`);
            } else {
                csvAsString += `"${fee.label.trim()}","processor","${fee.totalAmount}","${fee.proposedTotalAmount}","${fee.totalVolume}","${fee.totalTransactions}","","","",""\n`;
            }
        });

        fees.additional.fees?.forEach(fee => csvAsString += `"${fee.label.trim()}","additional","${fee.amount}","${fee.proposedAmount}","","","","","",""\n`);
        fees.assessment.fees?.forEach(fee => csvAsString += `"${fee.label.trim()}","assessment","${fee.amount}","${fee.proposedAmount}","","","","","",""\n`);
        fees.nonNegotiable.fees?.forEach(fee => csvAsString += `"${fee.label.trim()}","nonNegotiable","${fee.amount}","${fee.proposedAmount}","","","","","",""\n`);
        fees.interchange.fees?.forEach(fee => csvAsString += `"${fee.label.trim()}","interchange","${fee.amount}","${fee.proposedAmount}","","","","","",""\n`);

        return csvAsString;
    }

    exportSummaryAsCsv(costComparisons: CostComparison[]): string {
        let csvAsString = '"label","summaryCurrentFees","summaryProposedFees","summaryMonthlySaving","summaryAnnualSavings","summaryCurrentEffectiveRate","summaryProposedEffectiveRate"\n';
        const savingsTotalMonthlyFees = this.feesSummaryService.getSavingsTotalMonthlyFees(costComparisons);
        
        csvAsString += `"summary","${_.round(this.feesSummaryService.getSummaryAverageCurrentTotalMonthlyFees(costComparisons), 2)}",`
            + `"${(_.round(this.feesSummaryService.getSummaryAverageProposedTotalFees(costComparisons), 2)).toString()}",`
            + `"${_.round(savingsTotalMonthlyFees, 2).toString()}",`
            + `"${_.round((savingsTotalMonthlyFees * 12), 2).toString()}",`
            + `"${_.round(this.feesSummaryService.getCurrentEffectiveRateSummary(costComparisons), 4).toString()}",`
            + `"${_.round(this.feesSummaryService.getProposedEffectiveRateSummary(costComparisons), 4).toString()}"\n`;
        return csvAsString;
    }
}
